import { useEffect, useRef, useState } from 'react';
import { AdyenCheckout, Dropin, Card, PayPal, GooglePay, ApplePay } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';

import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  createPaymentSession,
  getPaymentStatus,
} from '@/modules/templates/services/guest-payment.service';

interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  onPaid: () => void;
}

type Phase = 'idle' | 'creating-session' | 'awaiting-payment' | 'polling' | 'success' | 'error';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

const PaymentDialog = ({ open, onOpenChange, documentId, onPaid }: PaymentDialogProps) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartedAtRef = useRef<number>(0);

  // Mount Drop-in when dialog opens.
  //
  // adyen-web v6 mount API (per upgrade-to-web-v6 docs and SDK source):
  //   const checkout = await AdyenCheckout({...});
  //   const dropin = new Dropin(checkout, dropinConfig);
  //   dropin.mount(container);
  //
  // The v5 `checkout.create('dropin', ...)` API was removed; Core no
  // longer exposes `create()`. paymentMethodsConfiguration also moved
  // off CoreConfiguration onto DropinConfiguration (the second arg to
  // `new Dropin`).
  //
  // We hold the Drop-in instance in a closure variable (not a ref)
  // because it only needs to live for the lifetime of this effect, and
  // the cleanup function captures it via closure for unmount.
  useEffect(() => {
    if (!open) return;
    let ignore = false;
    let dropin: { unmount?: () => void } | null = null;

    const start = async () => {
      setPhase('creating-session');
      setError(null);
      try {
        const session = await createPaymentSession(documentId);
        if (ignore) return;
        const checkout = await AdyenCheckout({
          environment: session.environment.toLowerCase() as 'test' | 'live',
          clientKey: session.clientKey,
          session: { id: session.id, sessionData: session.sessionData },
          // countryCode is mandatory in v6 (per the upgrade guide).
          countryCode: session.countryCode,
          locale: 'en_US',
          showPayButton: true,
          onPaymentCompleted: (result: { resultCode?: string }) => {
            if (result?.resultCode === 'Authorised') {
              setPhase('polling');
              startPolling();
            } else {
              setError(`Payment status: ${result?.resultCode ?? 'unknown'}`);
              setPhase('error');
            }
          },
          onError: (err: Error) => {
            console.error('[Adyen Drop-in] error:', err);
            setError(err?.message ?? 'Payment error');
            setPhase('error');
          },
        });

        if (ignore) return;
        if (!containerRef.current) return;

        dropin = new Dropin(checkout, {
          paymentMethodComponents: [Card, PayPal, GooglePay, ApplePay],

          paymentMethodsConfiguration: {
            card: {
              hasHolderName: true,
              holderNameRequired: true,
              name: 'Credit or debit card',
              amount: session.amount,
            },
          },
        }).mount(containerRef.current);

        setPhase('awaiting-payment');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to start payment';
        setError(msg);
        setPhase('error');
      }
    };

    void start();

    return () => {
      ignore = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      try {
        dropin?.unmount?.();
      } catch {
        // ignore
      }
      dropin = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId]);

  const startPolling = () => {
    pollStartedAtRef.current = Date.now();
    const poll = async () => {
      try {
        const status = await getPaymentStatus(documentId);
        if (status.isPaid) {
          setPhase('success');
          onPaid();
          return;
        }
      } catch (err) {
        console.warn('[PaymentDialog] poll error', err);
      }

      const elapsed = Date.now() - pollStartedAtRef.current;
      if (elapsed >= POLL_TIMEOUT_MS) {
        setError(
          'Payment is processing — please refresh in a minute. If the document remains locked, try paying again.'
        );
        return;
      }
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };
    pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  };

  const handleClose = (next: boolean) => {
    if (!next && pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] sm:w-full max-h-[90vh] z-999 flex flex-col overflow-hidden sm:max-w-120 p-4 sm:p-6">
        <div className="shrink-0">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg pr-6">Pay $5.00 to unlock</DialogTitle>
            <DialogDescription>
              One-time payment unlocks Download, Edit, and Letterhead for this document.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          {phase === 'creating-session' && (
            <div className="text-sm text-globe-grey">Preparing payment…</div>
          )}

          <div ref={containerRef} className={phase === 'success' ? 'hidden' : ''} />

          {phase === 'polling' && (
            <div className="text-sm text-globe-grey mt-3">Confirming your payment…</div>
          )}

          {phase === 'success' && (
            <div className="text-sm text-emerald-600 font-medium">
              Payment confirmed — unlocking your document.
            </div>
          )}

          {phase === 'error' && error && <div className="text-sm text-red-600 mt-2">{error}</div>}
        </div>

        <div className="shrink-0 flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => handleClose(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
